/**
 * iter350 — selecting a weight goal.
 *
 * The bug: every goal tile set the start date only `if (!weightGoalStartDate)`.
 * Switching from Lose (started 10 weeks ago) to Mini-Cut therefore kept the old
 * date, so the new goal began life at "week 11" — past the end of its 4-week
 * schedule, deficit 0, i.e. maintenance macros under a Mini-Cut badge.
 *
 * A goal change now always restarts the clock; re-picking the goal you are
 * already on leaves it alone (so you can tap it without resetting your week).
 */
import { getToken } from './tokenStorage';
import { enqueueWrite } from './offlineQueue';

export const GOAL_KEY = 'gym_notebook_weight_goal';
export const GOAL_START_KEY = 'gym_notebook_weight_goal_start';
/** iter353 — epoch ms of the last LOCAL goal change, so a profile sync can tell
 *  whether the server copy is older than what the user just picked. */
export const GOAL_UPDATED_AT_KEY = 'gym_notebook_weight_goal_updated_at';

export const todayKey = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export const GOAL_CHANGED_EVENT = 'fitness:weight-goal-changed';

/** iter353 — should a synced server profile overwrite the local goal?
 *  Only when the server's own GOAL stamp is at least as new as this device's
 *  pick. A failed/offline PUT, a stale IndexedDB cache or a legacy row with no
 *  goal stamp used to win and silently restore the previous goal, leaving
 *  maintenance macros under a "Mini-Cut" badge. The document-wide `updated_at`
 *  is deliberately NOT accepted — every partial PUT bumps it. */
export function serverGoalWins(
  serverGoalUpdatedAt: string | null | undefined,
  localUpdatedAtMs: number,
): boolean {
  if (!(localUpdatedAtMs > 0)) return true;
  const serverMs = Date.parse(String(serverGoalUpdatedAt || '')) || 0;
  return serverMs >= localUpdatedAtMs;
}

/** Push the goal to the server with one retry. Fire-and-forget PUTs that
 *  silently failed were the other half of the "my Mini-Cut reverted" bug.
 *  The server echoes its own `weightGoalUpdatedAt`, which replaces the local
 *  `Date.now()` stamp so the precedence check compares two SERVER clocks (a
 *  device running a few minutes fast could otherwise outrank a newer pick
 *  made on another device).
 *
 *  iter355 — a push made while another is in flight used to be DROPPED, so two
 *  quick picks (or any pick during the ~3s offline retry window) saved the
 *  FIRST goal and the sync later undid the user's real choice. The latest pick
 *  always wins now: it is re-pushed as soon as the in-flight request settles. */
let goalPushInFlight = false;
let latestGoalPush: { goal: string; start: string } | null = null;

export async function pushWeightGoal(goal: string, start: string): Promise<void> {
  latestGoalPush = { goal, start };
  if (goalPushInFlight) return;
  goalPushInFlight = true;
  try {
    while (latestGoalPush) {
      const pending = latestGoalPush;
      await sendGoalOnce(pending.goal, pending.start);
      // Only clear when nothing newer arrived while we were sending.
      if (latestGoalPush === pending) latestGoalPush = null;
    }
  } finally {
    goalPushInFlight = false;
  }
}

async function sendGoalOnce(goal: string, start: string): Promise<void> {
  const token = getToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const body = JSON.stringify({ weightGoal: goal, weightGoalStartDate: start });
  const url = `${process.env.REACT_APP_BACKEND_URL || ''}/api/data/user-bodyweight`;
  const queue = () => enqueueWrite(url, 'PUT', body, {
    label: 'Weight goal', dedupeKey: 'PUT:/api/data/user-bodyweight-goal',
  });

  // Known-offline: skip the retry loop entirely so a second pick isn't stuck
  // behind 3s of pointless waiting.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    await queue();
    return;
  }
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const r = await fetch(url, {
        method: 'PUT', headers, body, credentials: 'include' as RequestCredentials,
      });
      if (r.ok) {
        const stamp = Date.parse(String((await r.json().catch(() => ({})))?.weightGoalUpdatedAt || ''));
        if (stamp) localStorage.setItem(GOAL_UPDATED_AT_KEY, String(stamp));
        return;
      }
    } catch { /* offline */ }
    await new Promise(res => setTimeout(res, 1500));
  }
  // Still unreachable — park it so the pick is not lost (iter354).
  await queue();
}

export function applyWeightGoal(
  goal: string,
  currentGoal: string,
  currentStart: string,
  setGoal: (g: string) => void,
  setStart: (d: string) => void,
  /** Force a fresh start date even when the goal is unchanged (Restart button). */
  restart = false,
): void {
  setGoal(goal);
  localStorage.setItem(GOAL_KEY, goal);
  localStorage.setItem(GOAL_UPDATED_AT_KEY, String(Date.now()));

  if (goal === 'maintain') {
    setStart('');
    localStorage.setItem(GOAL_START_KEY, '');
    window.dispatchEvent(new Event(GOAL_CHANGED_EVENT));
    return;
  }

  if (restart || goal !== currentGoal || !currentStart) {
    const today = todayKey();
    setStart(today);
    localStorage.setItem(GOAL_START_KEY, today);
  }
  // iter352c — dashboard tiles cache their targets; tell them to recompute.
  window.dispatchEvent(new Event(GOAL_CHANGED_EVENT));
}